/** Shared representation of evidence-bound episodes. No emotion scores or inferred history. */
export const LIVED_EXPERIENCE_POLICY = "lived-experience-2026-09-22-v1" as const;
export type LivedExperiencePolicy = typeof LIVED_EXPERIENCE_POLICY | "legacy-v1";
export interface LivedSource { chapter: number; hash: string }
export interface LivedAnchor extends LivedSource { key: string; evidence: string }
export interface ExperienceTrace { response: string; carryover: string; status: "active" | "settled" }
export interface KnowledgeTrace {
  information: string;
  acquisition: "witnessed" | "heard" | "read" | "inferred";
  reliability: "established" | "unverified";
  from?: string;
}
export interface LivedCause {
  anchor: LivedAnchor;
  relation: "cause" | "enables" | "motivates";
  perspective: "explicit" | "belief";
  holder?: string;
}
export interface StoryMoment {
  label: string;
  relation: "at" | "before" | "after" | "during" | "unknown";
  anchor?: LivedAnchor;
  evidence: string;
}
/** Stored within the existing timeline ledger, not a separate fact store.
 * source is assigned by the server; unbound candidates must never be published. */
export interface LivedMemory {
  version: 1;
  source?: LivedSource;
  actor: string;
  event: string;
  perspective: "observed" | "self_report" | "belief" | "intention";
  experience?: ExperienceTrace;
  knowledge?: KnowledgeTrace;
  causes: LivedCause[];
  time?: StoryMoment;
}
export interface LivedActor { key: string; name: string; aliases: string[] }
export interface HistoricalKnowledgeSource extends LivedAnchor {
  /** Actual supplied prose, not a summary or a planned discovery. */
  text: string;
  holder?: string;
}
export interface LivedContextReceipt {
  policy: typeof LIVED_EXPERIENCE_POLICY;
  throughChapter: number;
  actors: LivedActor[];
  sources: LivedAnchor[];
  omittedKeys: string[];
  ambiguous: Array<{ alias: string; candidates: string[] }>;
}
export const perspectiveNames = { observed: "正文明确呈现", self_report: "人物自述", belief: "人物所信", intention: "人物打算" };
export const acquisitionNames = { witnessed: "亲历", heard: "听闻", read: "阅读材料", inferred: "人物推断" };
export const timeRelationNames = { at: "明确时间", before: "早于", after: "晚于", during: "同期或区间内", unknown: "时间未确定" };
export function describeLivedMemory(memory: LivedMemory): string[] {
  return [
    `${memory.actor} · ${perspectiveNames[memory.perspective]}：${memory.event}`,
    ...(memory.experience ? [`当时反应：${memory.experience.response}`, `经历余波（${memory.experience.status === "active" ? "仍有文本依据，不按章数衰减" : "正文已呈现落定"}）：${memory.experience.carryover || "没有明确记录持续影响"}`] : []),
    ...(memory.knowledge ? [`获知：${memory.knowledge.information}；${acquisitionNames[memory.knowledge.acquisition]}${memory.knowledge.from ? `，来源 ${memory.knowledge.from}` : ""}；${memory.knowledge.reliability === "established" ? "按该持有人的证据成立，非全体已知" : "尚未证实，不得升级为客观真相"}`] : []),
    ...memory.causes.map(cause => `因果 ${cause.relation}：${cause.anchor.key} · 第${cause.anchor.chapter}章${cause.perspective === "belief" ? `（${cause.holder}的解释，并非已证实因果）` : "（正文明确关系）"}`),
    ...(memory.time ? [`故事时间：${memory.time.label}；${timeRelationNames[memory.time.relation]}${memory.time.anchor ? ` ${memory.time.anchor.key}` : ""}。章节号仅作出处，不换算时间。`] : [])
  ];
}
